import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { AuthShell, SubmitBtn, ErrorNote } from "./AuthShell";

const isAdminRole = (role) => ["Owner", "Administrator"].includes(role);

export default function AcceptPartner() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { user, memberships } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const adminOrgs = (memberships || []).filter((m) => isAdminRole(m.role));
  const [orgId, setOrgId] = useState("");

  useEffect(() => {
    if (!token) { setError("Lien d'invitation invalide."); return; }
    api.get(`/partner-invitations/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((err) => setError(formatApiError(err.response?.data?.detail, err.message)));
  }, [token]);

  useEffect(() => { if (adminOrgs.length && !orgId) setOrgId(adminOrgs[0].org.id); }, [adminOrgs, orgId]);

  const accept = async () => {
    setError(""); setLoading(true);
    try {
      await api.post("/partner-invitations/accept", { token, org_id: orgId });
      navigate("/partners");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err.message));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Invitation de partenariat"
      subtitle={invite ? `${invite.org_name} souhaite établir un partenariat.` : "Vérification de l'invitation…"}>
      <div className="space-y-5" data-testid="accept-partner">
        <ErrorNote>{error}</ErrorNote>

        {invite && (
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Organisation invitante</p>
            <p className="mt-1 font-heading text-lg font-bold text-[#1E3D2B]" data-testid="partner-org">{invite.org_name}</p>
            {invite.org_type && <p className="mt-0.5 text-sm text-slate-500">{invite.org_type}</p>}
          </div>
        )}

        {!user ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Connectez-vous pour accepter avec l'une de vos organisations.</p>
            <div className="flex gap-3">
              <Link to="/login" className="flex h-11 flex-1 items-center justify-center rounded-lg bg-[#1E3D2B] text-sm font-semibold text-white hover:bg-[#2C5540]">Se connecter</Link>
              <Link to="/register" className="flex h-11 flex-1 items-center justify-center rounded-lg border border-slate-200 text-sm font-semibold text-[#1E3D2B] hover:bg-slate-50">Créer un compte</Link>
            </div>
          </div>
        ) : adminOrgs.length === 0 ? (
          <ErrorNote>Vous devez être Administrateur ou Propriétaire d'une organisation pour accepter un partenariat.</ErrorNote>
        ) : invite && (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Accepter en tant que</label>
              <select data-testid="accept-partner-org" value={orgId} onChange={(e) => setOrgId(e.target.value)}
                className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#5C9873]">
                {adminOrgs.map((m) => <option key={m.org.id} value={m.org.id}>{m.org.name} ({m.role})</option>)}
              </select>
            </div>
            <SubmitBtn loading={loading} onClick={accept} data-testid="accept-partner-btn">Établir le partenariat</SubmitBtn>
          </div>
        )}
      </div>
    </AuthShell>
  );
}
